import { Fragment, useCallback, useId, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import Divider from 'LuxKit/Divider'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import useToggle from 'hooks/useToggle'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import ChatListSectionLabel from 'tripPlanner/pages/chat/ChatListSectionLabel'
import ChatListShowMoreToggle from 'tripPlanner/pages/chat/ChatListShowMoreToggle'
import type { RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import ChatLandingRecentChatRow from './ChatLandingRecentChatRow'

const COLLAPSED_CHAT_COUNT = 3

// Eight and a half rows: eight chats fit whole, and a cut-off ninth row signals the list scrolls.
const EXPANDED_LIST_MAX_BLOCK_SIZE = rem(588)

// Phones scroll the whole landing instead: a nested scroller taller than the viewport traps swipes.
const ChatList = styled.div`
  ${mediaQueryUp.tablet} {
    max-block-size: ${EXPANDED_LIST_MAX_BLOCK_SIZE};
    overflow-y: auto;
  }
`

interface Props {
  items: Array<RecentChatItem>
  /** Epoch milliseconds the relative timestamps are measured from. */
  now: number
  onChatClick: () => void
}

function ChatLandingRecentChats({ items, now, onChatClick }: Props) {
  const intl = useIntl()
  const [showAll, toggleShowAll] = useToggle()
  const listId = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const [visibleItems] = useListTruncator(showAll ? undefined : COLLAPSED_CHAT_COUNT, items)

  const handleToggle = useCallback(() => {
    if (showAll && listRef.current) {
      listRef.current.scrollTop = 0
    }
    toggleShowAll()
  }, [showAll, toggleShowAll])

  const showMoreLabel = intl.formatMessage({
    id: 'OmowVeMsD7YCQ_Bs2Akq',
    defaultMessage: 'Show more',
    description: 'Link that reveals every recent chat on the trip planner chat landing page',
  })

  return (
    <Group direction="vertical" gap={12} fullWidth>
      <ChatListSectionLabel>
        <FormattedMessage {...tripPlannerGlossaryMessages.recentChats} />
      </ChatListSectionLabel>
      <ChatList id={listId} ref={listRef}>
        {visibleItems.map((item, index) => (
          <Fragment key={`${item.type}-${item.id}`}>
            {index > 0 && <Divider kind="primary" />}
            <ChatLandingRecentChatRow item={item} now={now} onClick={onChatClick} />
          </Fragment>
        ))}
      </ChatList>
      {items.length > COLLAPSED_CHAT_COUNT && (
        <ChatListShowMoreToggle
          expanded={showAll}
          controlsId={listId}
          collapsedLabel={showMoreLabel}
          onClick={handleToggle}
        />
      )}
    </Group>
  )
}

export default ChatLandingRecentChats
